import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Users, Mail, Phone, Calendar, Search, RefreshCw, CheckCircle,
  AlertCircle, DollarSign, UserCheck, Trash2
} from 'lucide-react';
import { getLegacyStatusBadgeClass, getLegacyPaymentBadgeClass } from './constants';

export default function LegacyTab({
  registrations, legacyLoading, processingIds,
  onConfirmPayment, onConfirmMatricula, onDelete,
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRegistrations = registrations.filter((reg) =>
    reg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.phone.includes(searchTerm)
  );

  const legacyStats = {
    total: registrations.length,
    confirmed: registrations.filter((reg) => reg.status === 'confirmed').length,
    paid: registrations.filter((reg) => reg.payment_status === 'paid').length,
    pending: registrations.filter((reg) => reg.status === 'pending').length,
  };

  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          <strong>Atenção:</strong> esta aba lista as pré-matrículas do curso antigo (legado).
          As novas inscrições dos cursos Eleições 2026 estão na aba "Compras Eleições 2026".
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-5 text-center">
          <Users className="w-7 h-7 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{legacyStats.total}</p>
          <p className="text-xs text-gray-600">Total Inscritos</p>
        </CardContent></Card>
        <Card><CardContent className="p-5 text-center">
          <CheckCircle className="w-7 h-7 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-600">{legacyStats.confirmed}</p>
          <p className="text-xs text-gray-600">Confirmadas</p>
        </CardContent></Card>
        <Card><CardContent className="p-5 text-center">
          <DollarSign className="w-7 h-7 text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-emerald-600">{legacyStats.paid}</p>
          <p className="text-xs text-gray-600">Pagas</p>
        </CardContent></Card>
        <Card><CardContent className="p-5 text-center">
          <AlertCircle className="w-7 h-7 text-yellow-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-yellow-600">{legacyStats.pending}</p>
          <p className="text-xs text-gray-600">Pendentes</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-lg">
            <span>Inscrições legadas ({filteredRegistrations.length})</span>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-full md:w-80" />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {legacyLoading ? (
            <div className="text-center py-8"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-500" /><p className="mt-2 text-gray-600">Carregando...</p></div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="text-center py-8"><Users className="w-12 h-12 text-gray-400 mx-auto mb-3" /><p className="text-gray-500">{searchTerm ? 'Nenhum resultado' : 'Nenhuma inscrição'}</p></div>
          ) : (
            <div className="space-y-3">
              {filteredRegistrations.map((reg) => (
                <LegacyRow
                  key={reg._id}
                  reg={reg}
                  processing={processingIds.has(reg._id)}
                  onConfirmPayment={onConfirmPayment}
                  onConfirmMatricula={onConfirmMatricula}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function LegacyRow({ reg, processing, onConfirmPayment, onConfirmMatricula, onDelete }) {
  const showConfirmPayment = reg.payment_status !== 'paid';
  const showConfirmMatricula = reg.status !== 'confirmed';

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-900">{reg.name}</h3>
            <Badge className={getLegacyStatusBadgeClass(reg.status)}>
              {reg.status === 'confirmed' ? 'Confirmado' : 'Pendente'}
            </Badge>
            <Badge className={getLegacyPaymentBadgeClass(reg.payment_status)}>
              {reg.payment_status === 'paid' ? 'Pago' : 'Pendente'}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{reg.email}</span>
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{reg.phone}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(reg.created_at).toLocaleString('pt-BR')}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {showConfirmPayment && (
            <Button size="sm" onClick={() => onConfirmPayment(reg._id, reg.name)} disabled={processing} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" />Pago</>}
            </Button>
          )}
          {showConfirmMatricula && (
            <Button size="sm" onClick={() => onConfirmMatricula(reg._id, reg.name)} disabled={processing} className="bg-blue-500 hover:bg-blue-600 text-white">
              {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><UserCheck className="w-4 h-4 mr-1" />Confirmar</>}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onDelete(reg._id, reg.name)} disabled={processing} className="border-red-200 text-red-600 hover:bg-red-50">
            {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
